// « Les signes de l'équipe » (D-161, choix 6 ; maquette d4 de D-162) : à côté de chaque geste dessiné par Arceau, ce
// qu'il veut dire chez nous (« Corne → Horns 4 »). Le staff de l'équipe les donne, les change, les retire (touche une
// ligne, ou « + un sens » sous un geste libre) ; les joueuses et joueurs de l'équipe les lisent, à côté des plays ; un
// parent ne les voit pas (la base en décide aussi : team_signs). « ▶ Lancer « Annonce du système » » : le modèle prêt,
// qui tire les signes qui ont un sens et affiche leur nom après 1 s.
import './drill-seq.css';
import type { Team } from '../lib/api';
import { setTeamSign, teamSigns, type TeamSign } from '../lib/signs-api';
import { SEQ_MODELS, defaultReaction } from '../domain/drills';
import { SIGNS, signOf, signSvg, type SignId } from '../domain/signs';
import { playerNoun } from '../domain/labels';
import { me, workTeams, type Ctx } from './context';
import { $, $$, html, mount, raw, toast, type Html } from './dom';
import { launchDrill } from './drill-run';

/** Une joueuse ou un joueur de l'équipe, en personne (pas son parent). */
export const isTeamPlayerSelf = (ctx: Ctx, teamId: string) => { const self = me(ctx); return !!self && ctx.memberships.some(m => m.team_id === teamId && m.person_id === self.id); };

/** Le lien depuis les plays : le staff, et les joueuses et joueurs de l'équipe quand un geste a un sens. */
export async function teamSignsLink(ctx: Ctx, team: Team): Promise<Html | ''> {
  const staff = ctx.staffOf.has(team.id);
  if (!staff && !isTeamPlayerSelf(ctx, team.id)) return '';
  const rows = await teamSigns(team.id).catch(() => [] as TeamSign[]);
  if (!staff && !rows.length) return '';
  const pics = (rows.length ? rows.map(r => r.sign) : ['corne', 'n2', 'tete'] as SignId[]).slice(0, 3);
  return html`<a class="dq-signlink" href="#/basket/signes/${team.id}" data-guide="ts-link"><span class="pics">${pics.map(id => raw(signSvg(id)))}</span>
    <span><strong>Les signes de l’équipe</strong><small>${rows.length ? `${rows.length} geste${rows.length > 1 ? 's ont' : ' a'} un sens chez nous` : 'Donne un sens aux gestes du meneur'}</small></span><span class="go" aria-hidden="true">›</span></a>`;
}

export async function renderTeamSigns(root: HTMLElement, ctx: Ctx, teamId: string | undefined): Promise<void> {
  const teams = workTeams(ctx);
  const team = teams.find(t => t.id === teamId) ?? teams[0];
  const staff = !!team && ctx.staffOf.has(team.id);
  if (!team || (!staff && !isTeamPlayerSelf(ctx, team.id))) {
    mount(root, html`<section class="screen"><div class="pp-nav"><a class="back" href="#/basket">‹ Plays</a></div>
      <div class="empty"><p class="empty-title">Réservé à l’équipe</p><p class="muted">Le staff et les ${playerNoun(team?.gender ?? 'F', 2)} de l’équipe voient ses signes.</p></div></section>`);
    return;
  }
  let rows = await teamSigns(team.id).catch(() => [] as TeamSign[]);
  let editing: SignId | null = null;
  const byId = () => new Map(rows.map(r => [r.sign, r]));

  const row = (id: SignId): Html => {
    const s = signOf(id)!, r = byId().get(id);
    if (staff && editing === id) return html`<li class="edit" data-guide="ts-edit">${raw(signSvg(id))}<span class="t"><span class="g">${s.label} · ${s.sub}</span>
        <input data-meaning maxlength="60" value="${r?.meaning ?? ''}" placeholder="ex. Horns 4" aria-label="Le sens de ce signe">
        <input class="d" data-detail maxlength="120" value="${r?.detail ?? ''}" placeholder="précision, facultatif" aria-label="Une précision"></span>
      <button type="button" class="ed" data-ok aria-label="Enregistrer"><span class="ti dq-i-check" aria-hidden="true"></span></button></li>`;
    return html`<li>${raw(signSvg(id))}<span class="t"><span class="g">${s.label}</span><strong>${r!.meaning}</strong>${r!.detail ? html`<small>${r!.detail}</small>` : ''}</span>
      ${staff ? html`<button type="button" class="ed" data-edit="${id}" aria-label="Changer le sens de ${s.label}"><span class="ti dq-i-pen" aria-hidden="true"></span></button>` : html`<span></span>`}</li>`;
  };
  const draw = () => {
    const have = byId();
    const listed = SIGNS.filter(s => have.has(s.id) || (staff && editing === s.id)).map(s => s.id);
    const free = SIGNS.filter(s => !have.has(s.id) && editing !== s.id);
    const n = have.size;
    mount(root, html`<section class="screen">
      <div class="pp-nav"><a class="back" href="#/basket/plays/${team.id}">‹ Plays</a><span class="chip chip-mute">${team.name}</span></div>
      <header class="screen-head"><p class="eyebrow">${team.name} · Basket</p><h2>Les signes de l’équipe</h2>
        <p class="muted">${staff ? 'Il n’existe pas de code commun : Arceau dessine les gestes, toi tu dis ce qu’ils veulent dire chez vous.' : 'Les gestes du coach, et ce qu’ils veulent dire chez nous.'}</p></header>
      <div class="dq-sechead"><h3 class="sec-title">Ont un sens · ${n}</h3>${staff && n ? html`<span class="muted small">touche pour changer</span>` : ''}</div>
      ${listed.length ? html`<ul class="dq-meanings" data-guide="ts-list">${listed.map(row)}</ul>`
        : html`<p class="muted" data-guide="ts-list">${staff ? 'Aucun geste n’a encore de sens : touche-en un ci-dessous.' : 'Le coach n’a pas encore donné de sens aux gestes.'}</p>`}
      ${staff && free.length ? html`<div class="dq-sechead"><h3 class="sec-title">Pas encore de sens · ${free.length}</h3></div>
        <div class="dq-free" data-guide="ts-free">${free.map(s => html`<button type="button" data-new="${s.id}">${raw(signSvg(s.id))}<b>${s.label}</b><small>+ un sens</small></button>`)}</div>` : ''}
      <p class="hint">${staff ? `Les ${playerNoun(team.gender, 2)} de l’équipe voient cette page sans pouvoir la changer ; les parents, non. Les drills peuvent tirer ces signes et afficher leur nom.`
        : 'Le coach les montre pendant le match ; dans les drills, le nom du système apparaît après le geste.'}</p>
      ${staff ? html`<button type="button" class="btn btn-primary" data-launch data-guide="ts-launch">▶ Lancer « Annonce du système »</button>` : ''}
    </section>`);
    wire();
  };
  const save = async () => {
    if (!editing) return;
    const id = editing;
    const m = $<HTMLInputElement>(root, '[data-meaning]')?.value ?? '', d = $<HTMLInputElement>(root, '[data-detail]')?.value ?? '';
    try {
      await setTeamSign(team.id, id, m, d);
      rows = await teamSigns(team.id);
      toast(m.trim() ? `« ${signOf(id)!.label} » : ${m.trim()}` : `« ${signOf(id)!.label} » n’a plus de sens`);
    } catch { toast('Enregistrement impossible.', 'ko'); return; }
    editing = null; draw();
  };
  const edit = (id: SignId) => {
    editing = id; draw();
    const i = $<HTMLInputElement>(root, '[data-meaning]');
    i?.focus(); i?.closest('li')?.scrollIntoView({ block: 'center' });
  };
  const wire = () => {
    for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) b.addEventListener('click', () => edit(b.dataset.edit as SignId));
    for (const b of $$<HTMLButtonElement>(root, '[data-new]')) b.addEventListener('click', () => edit(b.dataset.new as SignId));
    $(root, '[data-ok]')?.addEventListener('click', () => void save());
    for (const i of $$<HTMLInputElement>(root, '[data-meaning], [data-detail]')) i.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); void save(); }
      if (e.key === 'Escape') { editing = null; draw(); }
    });
    $(root, '[data-launch]')?.addEventListener('click', () => {
      const m = SEQ_MODELS.find(x => x.key === 'systeme')!;
      void launchDrill({ id: '', club_id: team.club_id, title: m.name, kind: 'reaction', config: { ...defaultReaction(), mode: 'sequence', sequence: m.make() } as unknown as Record<string, unknown> },
        { team, staff: true, trial: true });
    });
  };
  draw();
}
